import React from 'react';
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Paper, TableSortLabel, Skeleton } from '@mui/material';

export interface Column<T> {
  id: keyof T | string;
  label: string;
  minWidth?: number;
  align?: 'inherit' | 'left' | 'center' | 'right' | 'justify';
  format?: (value: any, row: T) => React.ReactNode;
}

export interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  loading?: boolean;
  page: number;
  rowsPerPage: number;
  total: number;
  orderBy?: string;
  order?: 'asc' | 'desc';
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rpp: number) => void;
  onRequestSort?: (columnId: string) => void;
}

function DataTable<T extends Record<string, any>>({ columns, rows, loading, page, rowsPerPage, total, orderBy, order = 'asc', onPageChange, onRowsPerPageChange, onRequestSort }: DataTableProps<T>) {
  const handleSort = (columnId: string) => {
    if (onRequestSort) onRequestSort(columnId);
  };

  const totalMinWidth = columns.reduce((acc, col) => acc + (col.minWidth || 100), 0);

  return (
    <Paper sx={{ 
      width: '100%', 
      maxWidth: '100%',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      mx: 0,
      px: 0
    }}>
      <TableContainer sx={{ 
        maxHeight: 'calc(100vh - 300px)',
        overflowX: { xs: 'auto', md: 'visible' },
        overflowY: 'auto',
        width: '100%',
        maxWidth: '100%',
        '&::-webkit-scrollbar': {
          height: '8px',
          width: '8px',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: 'rgba(0,0,0,0.1)',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(0,0,0,0.3)',
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
          backgroundColor: 'rgba(0,0,0,0.5)',
        },
      }}>
        <Table stickyHeader sx={{ 
          minWidth: totalMinWidth,
          width: '100%'
        }}>
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell 
                  key={String(col.id)} 
                  align={col.align} 
                  sx={{ 
                    minWidth: col.minWidth,
                    whiteSpace: 'nowrap',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    width: col.id === 'name' ? '25%' : 
                           col.id === 'identifier' ? '20%' :
                           col.id === 'credit' ? '15%' :
                           col.id === 'endIn' ? '15%' :
                           col.id === 'active' ? '10%' :
                           col.id === 'actions' ? '15%' : 'auto'
                  }}
                >
                  {onRequestSort ? (
                    <TableSortLabel
                      active={orderBy === col.id}
                      direction={orderBy === col.id ? order : 'asc'}
                      onClick={() => handleSort(String(col.id))}
                      sx={{ fontSize: '0.875rem' }}
                    >
                      {col.label}
                    </TableSortLabel>
                  ) : (
                    col.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading
              ? Array.from({ length: rowsPerPage }).map((_, i) => (
                  <TableRow key={`skeleton-${i}`}>
                    {columns.map((c, j) => (
                      <TableCell key={`s-${i}-${j}`} sx={{ fontSize: '0.875rem' }}>
                        <Skeleton variant="text" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : rows.map((row, i) => (
                  <TableRow hover role="checkbox" tabIndex={-1} key={i}>
                    {columns.map((col) => {
                      const raw = row[col.id as keyof T];
                      return (
                        <TableCell 
                          key={String(col.id)} 
                          align={col.align}
                          sx={{ 
                            fontSize: '0.875rem',
                            whiteSpace: 'nowrap',
                            minWidth: col.minWidth,
                            width: col.id === 'name' ? '25%' : 
                                   col.id === 'identifier' ? '20%' :
                                   col.id === 'credit' ? '15%' :
                                   col.id === 'endIn' ? '15%' :
                                   col.id === 'active' ? '10%' :
                                   col.id === 'actions' ? '15%' : 'auto'
                          }}
                        >
                          {col.format ? col.format(raw, row) : String(raw ?? '')}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={columns.length} align="center" sx={{ py: 4 }}>
                  Nenhum registro encontrado
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'flex-end',
        borderTop: '1px solid',
        borderColor: 'divider',
        backgroundColor: 'background.paper',
        width: '100%',
        maxWidth: '100%',
        overflow: 'hidden'
      }}>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => onPageChange(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => onRowsPerPageChange(parseInt(e.target.value, 10))}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage="Linhas por página"
          sx={{
            '& .MuiTablePagination-toolbar': {
              paddingLeft: 1,
              paddingRight: 1,
            },
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
              fontSize: '0.875rem',
            }
          }}
        />
      </Box>
    </Paper>
  );
}

export default DataTable;


